package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.model.DayWeather
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.weather.ObserveWorkplaceWeatherUseCase
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

/**
 * SH-226: the dashboards' "Weather in the coming days": whether the workplace has weather on (and a
 * location), and the forecast from today on as the server wrote it.
 */
data class UpcomingWeatherState(
    /** Weather is on and the workplace has a location: the card shows (empty until the first forecast). */
    val on: Boolean = false,
    val days: List<DayWeather> = emptyList(),
    val today: LocalDate? = null,
    /**
     * SH-247: whether the workplace's weather setting was read: until then [on] false means "not
     * known yet" (a placeholder while the dashboard loads), after it "no card".
     */
    val known: Boolean = false
)

/**
 * Follows one workplace's forecast at a time for a dashboard view model: a new workplace (or a
 * reload) restarts it; the same workplace keeps its listener.
 */
class UpcomingWeather(
    private val observe: ObserveWorkplaceWeatherUseCase?,
    private val workplaces: WorkplaceRepository?,
    private val today: () -> LocalDate
) {
    private var job: Job? = null
    private var following: String? = null

    fun follow(scope: CoroutineScope, workplaceId: String, restart: Boolean = false, onChange: (UpcomingWeatherState) -> Unit) {
        val observe = observe
        val workplaces = workplaces
        // SH-247: no forecast here at all: known, no card (never a placeholder left behind).
        if (observe == null || workplaces == null) return onChange(UpcomingWeatherState(known = true))
        if (!restart && following == workplaceId && job?.isActive == true) return
        following = workplaceId
        job?.cancel()
        job = scope.launch {
            val workplace = workplaces.getWorkplace(workplaceId).getOrNull()
            val on = workplace != null && ObserveWorkplaceWeatherUseCase.hasWeather(workplace)
            val day = today()
            onChange(UpcomingWeatherState(on = on, today = day, known = true))
            if (!on) return@launch
            observe.upcoming(workplace, day).collect { days ->
                onChange(UpcomingWeatherState(on = true, days = ObserveWorkplaceWeatherUseCase.upcomingDays(days, day), today = day, known = true))
            }
        }
    }
}
